import { fetchConfig } from '@theme/utilities';
import { resolveBuilderCloseMode } from '@theme/pb-builder-close-mode';
import { refreshCartSections } from '@theme/section-renderer';
import {
  ensureFoundationModulesReady,
  ensureEncodeBeltModulesReady,
  getShopRoot,
  getThemeRoutes,
  installCloseResumeHook,
  getCartSectionIds,
  restoreBuilderRootToPortal,
} from '@theme/pb-builder-launch';
import { ensureModulesReadyForEntryManifest } from '@theme/pb-entry-readiness';
import { bootBuilderOpen, recoverBuilderScrollLock, recoverStaleBuilderChrome } from '@theme/pb-builder-boot';
import {
  captureOpenFailure,
  createCartEditPresenter,
} from '@theme/pb-cart-edit-presenter';
import {
  isChildLineCartEditTarget,
  resolveAnchorProductMount,
  resolveAnchorCartLineForChildEdit,
} from '@theme/pb-cart-edit-anchor';
import {
  applyEntryManifest,
  createMainCartEditEntryManifest,
  createSubAddonCartEditEntryManifest,
  createSubBundleCartEditEntryManifest,
  HYDRATION_AUTHORITY,
  HYDRATION_SOURCES,
  isLineAuthoritativeManifest,
  isChildLineCartEditManifest,
  readEntryManifest,
} from '@theme/pb-entry-manifest';
import {
  buildChildLineCartEditContext,
  CART_EDIT_FLOW,
  getMaxChildLineSteps,
  isChildLineCartEdit,
  isChildLineCartSubPresentation,
  resolveBundleRowCatalogItem,
} from '@theme/pb-cart-child-edit';
import {
  applyUploadUrlsToWrap,
  hydrateMainFlowMediaFromState,
} from '@theme/pb-media-hydrate';
import { hydrateDesignLibraryAndAiFromState, applyCatalogDesignLibraryToState, applyAiImageToState, applyAiTextToState, buildAiTextDraftFromCartProps, deserializeAiImageFieldState, hydrateAiTextFromDraft, enrichCatalogDesignLibraryFromTier1Cart } from '@theme/pb-dl-ai-hydrate';
import { decodeCatalogDesignLibraryFromProps } from '@theme/pb-design-library-snapshot';
import { assertEncodeBeltReady } from '@theme/pb-flush-encode';
import { loadUsableCartSessionViaStore } from '@theme/pb-session-wire';
import {
  CART_FAMILY_KEYS,
  assertLegacyBundleUnambiguous,
  buildAddonsMetadataString,
  discoverFamily,
  snapshotFamilySources,
  hydrateAddonSelectionsFromFamily,
  isAddonCartLine,
  isBundleChildCartLine,
  isOfferSwitch,
  resolveBundleByTitle,
  resolveBundleRowIndexInCart,
  resolveFamilyAnchorLineKey,
  stripSystemProperties as stripFamilySystemProperties,
} from '@theme/pb-cart-family-sync';

function getShopifyBundleInherit() {
  return typeof window !== 'undefined' ? window.PBShopifyBundleInherit : null;
}

function isShopifyBundleCartEditContext(builderData) {
  return !!(builderData && builderData.shopifyBundle === true);
}

const SYSTEM_PROP_KEYS = { _Bundle: true, 'Add-ons': true, 'For Item': true };
const PRESERVED_CART_METADATA_KEYS = ['_Bundle', 'For Item', 'Add-ons', '_senderSurpriseId', '_senderSurpriseProof', ...CART_FAMILY_KEYS];
let openInFlight = null;

function isEditableCartLine(item) {
  return !!item;
}

function fetchCart() {
  return fetch(getShopRoot() + 'cart.js', { credentials: 'same-origin', cache: 'no-store' }).then((res) => {
    if (!res.ok) throw new Error('Could not load cart');
    return res.json();
  });
}

function findVariantById(variants, variantId) {
  const idStr = String(variantId);
  return (variants || []).find((v) => String(v.id) === idStr) || null;
}

function applyVariantToSelections(selections, options, variant) {
  if (!variant || !options) return;
  options.forEach((opt, idx) => {
    if (!opt || opt.name === 'Title') return;
    if (variant.options?.[idx] != null) selections[opt.name] = variant.options[idx];
  });
}

function buildAiImageDraftFromCartProps(pb, item, product) {
  if (!product || typeof pb?.aiImageFromSyncedProps !== 'function') return null;
  const props = expandCartLinePropsForEdit(pb, item?.properties || {});
  return pb.aiImageFromSyncedProps(props, product) || null;
}

function resolveCartEditAiImageDraft(pb, item, product, partial) {
  if (partial?.aiImage?.byLabel) return partial.aiImage;
  return buildAiImageDraftFromCartProps(pb, item, product);
}

function resolveCartEditAiTextDraft(item, product, partial, selections) {
  if (partial?.aiText?.byLabel) return partial.aiText;
  const fields = product?.config || product?.personalization || [];
  return buildAiTextDraftFromCartProps(item?.properties || {}, fields, selections || {});
}

function hydrateCartEditAiTextFromDraft(rootSelector, aiTextDraft, options) {
  if (!aiTextDraft?.byLabel) return;
  const fields =
    window.BUILDER_DATA?.mainProduct?.config ||
    window.productBuilder?.getPersonalizationFields?.(window.BUILDER_DATA?.mainProduct) ||
    [];
  applyAiTextToState(window.builderState, aiTextDraft, fields);
  hydrateDesignLibraryAndAiFromState({
    root: rootSelector,
    aiText: aiTextDraft,
    forceAiReinit: !!(options && options.forceAiReinit),
    skipDlResume: true,
  });
}

function hydrateCartEditAiFromDraft(rootSelector, aiImageDraft, options) {
  if (!aiImageDraft?.byLabel) return;
  const fields =
    window.BUILDER_DATA?.mainProduct?.config ||
    window.productBuilder?.getPersonalizationFields?.(window.BUILDER_DATA?.mainProduct) ||
    [];
  applyAiImageToState(window.builderState, aiImageDraft, fields);
  hydrateDesignLibraryAndAiFromState({
    root: rootSelector,
    aiImage: aiImageDraft,
    forceAiReinit: !!(options && options.forceAiReinit),
    skipDlResume: true,
  });
}

function stripSystemProperties(props) {
  const out = {};
  if (!props || typeof props !== 'object') return out;
  Object.keys(props).forEach((key) => {
    if (!key || key.charAt(0) === '_' || SYSTEM_PROP_KEYS[key]) return;
    out[key] = props[key];
  });
  return out;
}

function inferTopicSelectionsForEdit(pb, selections, topicSelections, fields, props) {
  let topics = { ...(topicSelections || {}) };
  if (Object.keys(topics).length) return topics;

  if (pb?.inferTopicSelectionsFromAnswers) {
    const ctx = { selections, topicSelections: {} };
    pb.inferTopicSelectionsFromAnswers(ctx, fields);
    if (Object.keys(ctx.topicSelections || {}).length) return { ...ctx.topicSelections };
  }

  if (pb?.parseTopicSelectionsBackup) {
    const fromBackup = pb.parseTopicSelectionsBackup(props);
    if (fromBackup) return { ...fromBackup };
  }

  return topics;
}

function applyDecodedLineHydration(pb, item, decoded) {
  const mainProduct = window.BUILDER_DATA?.mainProduct;
  const props = item.properties || {};
  const fromLine = stripSystemProperties(expandCartLinePropsForEdit(pb, props));
  let selections = { ...(decoded?.selections || {}), ...fromLine };
  let variantId = decoded?.variantId != null ? decoded.variantId : item.variant_id;

  const dlLogic = window.PBDesignLibraryLogic;
  if (dlLogic?.mergeDesignLibraryCartPropsIntoSelections) {
    selections = dlLogic.mergeDesignLibraryCartPropsIntoSelections(props, selections);
  }

  const variant = findVariantById(window.BUILDER_DATA?.variants, variantId);
  applyVariantToSelections(selections, window.BUILDER_DATA?.options, variant);

  if (pb.nestBundleSelectionsForAiImageFields && mainProduct) {
    selections = pb.nestBundleSelectionsForAiImageFields(mainProduct, selections);
  }

  const fields = pb.getPersonalizationFields?.(mainProduct) || [];
  const topicSelections = inferTopicSelectionsForEdit(
    pb,
    selections,
    decoded?.topicSelections,
    fields,
    props
  );

  return {
    selections,
    variantId,
    topicSelections,
    aiImage: buildAiImageDraftFromCartProps(pb, item, mainProduct),
    catalogDesignLibrary: decoded?.catalogDesignLibrary || null,
  };
}

function hydrateSelectionsFromSession(pb, item, partial) {
  const mainProduct = window.BUILDER_DATA?.mainProduct;
  const props = item.properties || {};
  const fromLine = stripSystemProperties(expandCartLinePropsForEdit(pb, props));
  let selections = { ...(partial?.main?.selections || {}), ...fromLine };
  let topicSelections = { ...(partial?.main?.topicSelections || {}) };
  let variantId = item.variant_id;

  const dlLogic = window.PBDesignLibraryLogic;
  if (dlLogic?.mergeDesignLibraryCartPropsIntoSelections) {
    selections = dlLogic.mergeDesignLibraryCartPropsIntoSelections(props, selections);
  }

  const variant = findVariantById(window.BUILDER_DATA?.variants, variantId);
  applyVariantToSelections(selections, window.BUILDER_DATA?.options, variant);

  if (pb.nestBundleSelectionsForAiImageFields && mainProduct) {
    selections = pb.nestBundleSelectionsForAiImageFields(mainProduct, selections);
  }

  const fields = pb.getPersonalizationFields?.(mainProduct) || [];
  topicSelections = inferTopicSelectionsForEdit(pb, selections, topicSelections, fields, props);

  return { selections, variantId, topicSelections, aiImage: partial?.aiImage || buildAiImageDraftFromCartProps(pb, item, mainProduct) };
}

function hydrateSelectionsFromLine(pb, item) {
  const mainProduct = window.BUILDER_DATA?.mainProduct;
  const fields = pb.getPersonalizationFields?.(mainProduct) || [];
  const options = window.BUILDER_DATA?.options || [];

  if (pb?.decodeCartLineToBuilderState) {
    const decoded = pb.decodeCartLineToBuilderState(item, fields, { options });
    return applyDecodedLineHydration(pb, item, decoded);
  }

  const props = item.properties || {};
  const fromLine = stripSystemProperties(expandCartLinePropsForEdit(pb, props));
  let selections = { ...fromLine };
  let variantId = item.variant_id;

  const dlLogic = window.PBDesignLibraryLogic;
  if (dlLogic?.mergeDesignLibraryCartPropsIntoSelections) {
    selections = dlLogic.mergeDesignLibraryCartPropsIntoSelections(props, selections);
  }

  const variant = findVariantById(window.BUILDER_DATA?.variants, variantId);
  applyVariantToSelections(selections, window.BUILDER_DATA?.options, variant);

  if (pb.nestBundleSelectionsForAiImageFields && mainProduct) {
    selections = pb.nestBundleSelectionsForAiImageFields(mainProduct, selections);
  }

  const topicSelections = inferTopicSelectionsForEdit(pb, selections, {}, fields, props);
  return {
    selections,
    variantId,
    topicSelections,
    aiImage: buildAiImageDraftFromCartProps(pb, item, mainProduct),
  };
}

function resolveCartEditHydration(pb, item) {
  const sessionResult = loadUsableCartSessionViaStore(item);
  const hasUsableSession =
    sessionResult?.partial &&
    !sessionResult.fingerprintMismatch &&
    !sessionResult.expired;

  if (hasUsableSession) {
    return {
      source: HYDRATION_SOURCES.SESSION,
      hydrated: hydrateSelectionsFromSession(pb, item, sessionResult.partial),
      partial: sessionResult.partial,
    };
  }
  return {
    source: HYDRATION_SOURCES.LINE,
    hydrated: hydrateSelectionsFromLine(pb, item),
    partial: null,
  };
}

/**
 * Apply MAIN cart edit selections per manifest hydration authority.
 * @param {object} pb
 * @param {object} item
 * @param {object} manifest
 * @param {{ hydrated: object, partial: object|null }} hydration
 */
function applyMainCartEditSelections(pb, item, manifest, hydration) {
  const s = window.builderState;
  if (!s?.main || !manifest) return;

  const builderData = window.BUILDER_DATA;
  const inherit = getShopifyBundleInherit();
  if (
    isShopifyBundleCartEditContext(builderData) &&
    inherit?.hydrateShopifyBundleCartEditFromLine
  ) {
    const bundleHydrated = inherit.hydrateShopifyBundleCartEditFromLine({
      pb,
      cartLine: item,
      builderData,
    });
    if (bundleHydrated) {
      s.main.selections = bundleHydrated.selections || {};
      s.main.topicSelections = bundleHydrated.topicSelections || {};
      return;
    }
  }

  if (isLineAuthoritativeManifest(manifest)) {
    const lineHydrated = hydrateSelectionsFromLine(pb, item);
    s.main.selections = lineHydrated.selections;
    s.main.topicSelections = lineHydrated.topicSelections || {};
  } else {
    s.main.selections = hydration.hydrated.selections;
    s.main.topicSelections = hydration.hydrated.topicSelections || {};
  }
}

function isMediaUrl(val) {
  if (typeof val !== 'string') return false;
  const v = val.trim();
  return v.startsWith('http://') || v.startsWith('https://');
}

function collectAiImageMediaKeys(pb, mainProduct) {
  const keys = new Set(['Photo', 'Character']);
  (mainProduct?.config || []).forEach((field) => {
    if (!field?.type || !String(field.type).toLowerCase().includes('ai image')) return;
    const pk = pb?.getAiImagePhotoKeyFromField?.(field);
    const ck = pb?.getAiImageCharacterKeyFromField?.(field);
    if (pk) keys.add(pk);
    if (ck) keys.add(ck);
    if (field.label) {
      keys.add(field.label);
      keys.add(`${field.label} - Photo`);
      keys.add(`${field.label} - Character`);
    }
  });
  return keys;
}

/** Direct cart line properties beat stale `_Photo_json` / numbered backups on edit. */
function expandCartLinePropsForEdit(pb, props) {
  const raw = props && typeof props === 'object' ? props : {};
  const expanded = pb?.expandDelimiterProperties ? pb.expandDelimiterProperties(raw) : { ...raw };
  const mainProduct = window.BUILDER_DATA?.mainProduct;
  collectAiImageMediaKeys(pb, mainProduct).forEach((key) => {
    const direct = raw[key];
    if (isMediaUrl(direct)) expanded[key] = direct.trim();
  });
  return expanded;
}

function readMediaUrlFromProps(pb, props, key, options) {
  if (!props || !key) return '';
  const preferDirect = options?.preferDirect;
  if (preferDirect) {
    const raw = props[key];
    if (isMediaUrl(raw)) return raw.trim();
    if (Array.isArray(raw)) {
      const hit = raw.find(isMediaUrl);
      if (hit) return hit.trim();
    }
  }
  if (pb?.readMultiValueProperty) {
    const list = pb.readMultiValueProperty(props, key);
    if (list?.length && isMediaUrl(list[0])) return list[0].trim();
  }
  const raw = props[key];
  if (Array.isArray(raw)) {
    const hit = raw.find(isMediaUrl);
    return hit ? hit.trim() : '';
  }
  if (isMediaUrl(raw)) return raw.trim();
  return '';
}

function getCartEditProps(pb) {
  return expandCartLinePropsForEdit(pb, window.builderState?.cartEdit?.preservedProps || {});
}

function readMediaUrlFromKeyList(pb, props, keys, options) {
  for (const key of keys) {
    const url = readMediaUrlFromProps(pb, props, key, options);
    if (url) return url;
  }
  if (pb?.normalizeBundleFieldLabel) {
    const normalizedKeys = [...new Set(keys.filter(Boolean))].map((k) =>
      pb.normalizeBundleFieldLabel(k)
    );
    for (const key of Object.keys(props || {})) {
      if (!normalizedKeys.includes(pb.normalizeBundleFieldLabel(key))) continue;
      const url = readMediaUrlFromProps(pb, props, key, options);
      if (url) return url;
    }
  }
  return '';
}

function resolveAiImageFieldForWrap(pb, mainProduct, wrap) {
  const label = wrap?.dataset?.label || '';
  const config = mainProduct?.config || [];
  if (label) {
    const exact = config.find((f) => f?.label === label);
    if (exact) return exact;
    if (pb?.normalizeBundleFieldLabel) {
      const want = pb.normalizeBundleFieldLabel(label);
      const norm = config.find(
        (f) => f?.label && pb.normalizeBundleFieldLabel(f.label) === want
      );
      if (norm) return norm;
    }
  }
  const aiFields = config.filter(
    (f) => f?.type && String(f.type).toLowerCase().includes('ai image')
  );
  return aiFields.length === 1 ? aiFields[0] : null;
}

function photoKeysForAiField(pb, field) {
  const pk = pb?.getAiImagePhotoKeyFromField?.(field) || 'Photo';
  const ck = pb?.getAiImageCharacterKeyFromField?.(field) || 'Character';
  const legacyPhoto = field?.label ? `${field.label} - Photo` : '';
  const keys = [pk, 'Photo', legacyPhoto];
  // Field label "Character" must not be used as a photo key (same product uses label === character key).
  if (field?.label && field.label !== ck) keys.push(field.label);
  return [...new Set(keys.filter(Boolean))];
}

function characterKeysForAiField(pb, field) {
  const ck = pb?.getAiImageCharacterKeyFromField?.(field) || 'Character';
  const legacyChar = field?.label ? `${field.label} - Character` : '';
  return [...new Set([ck, 'Character', legacyChar, field?.label].filter(Boolean))];
}

function resolvePhotoUrlForCartEdit(pb, mainProduct, field) {
  const props = getCartEditProps(pb);
  const ctx = window.builderState?.main;
  const keys = photoKeysForAiField(pb, field);

  const fromProps = readMediaUrlFromKeyList(pb, props, keys, { preferDirect: true });
  if (fromProps) return fromProps;

  if (pb?.extractAiImagePropsFromSelections && mainProduct && ctx?.selections) {
    const aiFlat = pb.extractAiImagePropsFromSelections(mainProduct, ctx.selections);
    const pk = pb?.getAiImagePhotoKeyFromField?.(field) || 'Photo';
    if (pk && isMediaUrl(aiFlat[pk])) return aiFlat[pk].trim();
    if (isMediaUrl(aiFlat.Photo)) return aiFlat.Photo.trim();
  }

  return '';
}

function resolveCharacterUrlForCartEdit(pb, mainProduct, field) {
  const props = getCartEditProps(pb);
  const ctx = window.builderState?.main;
  const keys = characterKeysForAiField(pb, field);

  const fromProps = readMediaUrlFromKeyList(pb, props, keys, { preferDirect: true });
  if (fromProps) return fromProps;

  if (pb?.extractAiImagePropsFromSelections && mainProduct && ctx?.selections) {
    const aiFlat = pb.extractAiImagePropsFromSelections(mainProduct, ctx.selections);
    const ck = pb?.getAiImageCharacterKeyFromField?.(field) || 'Character';
    if (ck && isMediaUrl(aiFlat[ck])) return aiFlat[ck].trim();
    if (isMediaUrl(aiFlat.Character)) return aiFlat.Character.trim();
  }

  return '';
}

function hydrateAiImageFieldFromCart(pb, mainProduct, wrap, field, partialEntry) {
  let payload = null;
  if (partialEntry) {
    payload = deserializeAiImageFieldState(partialEntry) || partialEntry;
  }
  const pUrl = resolvePhotoUrlForCartEdit(pb, mainProduct, field);
  const cUrl = resolveCharacterUrlForCartEdit(pb, mainProduct, field);
  if (!payload && !pUrl && !cUrl) return;

  if (!payload) {
    payload = { photoUrl: pUrl, characterUrl: cUrl };
  } else {
    if (!payload.photoUrl && pUrl) payload.photoUrl = pUrl;
    if (
      (!payload.characterUrls || !payload.characterUrls.length) &&
      !payload.characterUrl &&
      cUrl
    ) {
      payload.characterUrl = cUrl;
    }
  }

  if (window.PBAiImage?.rehydrateWrap) {
    window.PBAiImage.rehydrateWrap(wrap, payload);
    return;
  }

  const inner = wrap.querySelector('.pb-upload-wrap');
  const photoUrl = payload.photoUrl || pUrl;
  if (inner && photoUrl) {
    wrap._pbAiRestoring = true;
    try {
      applyUploadUrlsToWrap(inner, [photoUrl], pb);
    } finally {
      wrap._pbAiRestoring = false;
    }
  }
}

function hydrateAiImageUploadWraps(pb, ctx, mainProduct, aiImageDraft) {
  if (!mainProduct) return;
  const byLabel = aiImageDraft?.byLabel || {};

  document.querySelectorAll('#main-flow-container .pb-ai-image-wrap').forEach((wrap) => {
    const field = resolveAiImageFieldForWrap(pb, mainProduct, wrap);
    if (!field) return;
    const label = field.label || wrap.dataset?.label || '';
    hydrateAiImageFieldFromCart(pb, mainProduct, wrap, field, byLabel[label]);
  });
}

function hydrateDesignLibraryWraps() {
  if (window.PBDesignLibrary?.hydrateCartEdit) {
    window.PBDesignLibrary.hydrateCartEdit('#main-flow-container');
  }
}

/** Clear stale cart-edit presentation + media between opens (any edit order). */
function resetCartEditOpenState() {
  const s = window.builderState;
  if (!s) return;

  delete s._addonCartSubPresented;
  delete s._pendingAddonCartPresent;
  delete s.aiImageByLabel;
  delete s.aiTextByLabel;

  if (s.main) {
    s.main.step = 1;
    s.main.selections = {};
    s.main.topicSelections = {};
    s.main.touched = {};
  }

  if (s.sub) {
    s.sub.step = 1;
    s.sub.selections = {};
    s.sub.topicSelections = {};
    s.sub.touched = {};
    s.sub.currentId = null;
    s.sub.variantId = null;
    s.sub.isQueueItem = false;
    s.sub.tempUniqueKey = null;
    s.sub.returnTo = null;
    delete s.sub.aiImageByLabel;
    delete s.sub.aiTextByLabel;
    delete s.sub.catalogDesignLibrary;
  }
}

function resolveChildLineAiImageDraftForCartEdit(pb, s, edit, catalogItem) {
  const lineDraft = buildAiImageDraftFromCartProps(
    pb,
    { properties: edit?.preservedProps || {} },
    catalogItem
  );
  if (lineDraft?.byLabel && Object.keys(lineDraft.byLabel).length) {
    return lineDraft;
  }
  return s?.sub?.aiImageByLabel || null;
}

function hydrateCartEditMedia(options) {
  const opts = options || {};
  const pb = window.productBuilder;
  const ctx = window.builderState?.main;
  const mainProduct = window.BUILDER_DATA?.mainProduct;
  const root = document.querySelector('#main-flow-container');
  if (!pb || !ctx || !root) return;

  hydrateMainFlowMediaFromState({
    root: '#main-flow-container',
    resolveUrl: (label) =>
      readMediaUrlFromProps(pb, window.builderState?.cartEdit?.preservedProps, label),
  });

  const aiImageDraft =
    window.builderState?.aiImageByLabel ||
    (opts.aiImage ? { byLabel: opts.aiImage.byLabel || opts.aiImage } : null);
  const aiTextDraft =
    window.builderState?.aiTextByLabel ||
    (opts.aiText ? { byLabel: opts.aiText.byLabel || opts.aiText } : null);

  if (window.PBAiImage?.init) {
    window.PBAiImage.init('#main-flow-container', { force: !!opts.forceAiReinit });
  }
  if (window.PBAiText?.init) {
    window.PBAiText.init('#main-flow-container', { force: !!opts.forceAiReinit });
  }

  hydrateAiImageUploadWraps(pb, ctx, mainProduct, aiImageDraft);
  hydrateAiTextFromDraft(aiTextDraft, '#main-flow-container');
  hydrateDesignLibraryWraps();
}

function hydrateChildLineCartEditMedia(options) {
  const opts = options || {};
  const pb = window.productBuilder;
  const s = window.builderState;
  const edit = s?.cartEdit;
  if (!pb || !edit || !s?.sub) return;

  const catalogItem = resolveChildLineCatalogItem(s, edit, s._pbCartEditCart);
  if (!catalogItem) return;

  const aiImageDraft = resolveChildLineAiImageDraftForCartEdit(pb, s, edit, catalogItem);

  if (aiImageDraft?.byLabel) {
    applyAiImageToState(s, aiImageDraft, catalogItem.config || []);
  }

  if (window.PBAiImage?.init) {
    window.PBAiImage.init('#sub-flow-container', { force: !!opts.forceAiReinit });
  }

  hydrateDesignLibraryAndAiFromState({
    root: '#sub-flow-container',
    aiImage: aiImageDraft,
    forceAiReinit: !!opts.forceAiReinit,
    skipDlResume: true,
  });
}

function scheduleCartEditMediaHydration() {
  const scheduledState = window.builderState;
  const scheduledEdit = scheduledState?.cartEdit;
  setTimeout(() => {
    const s = window.builderState;
    if (!scheduledEdit || s !== scheduledState || s.cartEdit !== scheduledEdit) return;
    const manifest = readEntryManifest(s);
    if (isChildLineCartEditManifest(manifest)) {
      hydrateChildLineCartEditMedia({ forceAiReinit: true });
      return;
    }
    hydrateCartEditMedia({ forceAiReinit: true });
  }, 0);
}

function flushCartEditAiImageFromDom() {
  if (window.PBAiImage?.flushAllFromDom) {
    window.PBAiImage.flushAllFromDom('#main-flow-container');
    window.PBAiImage.flushAllFromDom('#sub-flow-container');
  }
}

/** Fail-safe: restore portal + dismiss modal after cart edit open failure — never leave blank page. */
function teardownFailedCartEditOpen(err) {
  const s = window.builderState;
  if (s) {
    s.cartEdit = null;
    delete s._addonCartSubPresented;
    delete s._pendingAddonCartPresent;
    if (s.sub) {
      s.sub.step = 1;
      s.sub.currentId = null;
    }
    s.mode = 'MAIN';
  }

  recoverBuilderScrollLock();
  recoverStaleBuilderChrome();
  restoreBuilderRootToPortal();

  const portal = document.getElementById('pb-cart-edit-portal');
  const root = document.getElementById('product-builder-root');
  const modal = document.getElementById('builder-modal');

  if (root && portal && !portal.contains(root) && !root.closest('[id^="shopify-section-"]')) {
    portal.appendChild(root);
  }
  if (portal) {
    portal.hidden = true;
    portal.setAttribute('aria-hidden', 'true');
  }

  const mainCont = document.getElementById('main-flow-container');
  const subCont = document.getElementById('sub-flow-container');
  if (mainCont) mainCont.style.display = '';
  if (subCont) subCont.style.display = '';

  const pb = window.productBuilder;
  const wasInteractive = document.body.classList.contains('builder-open');

  if (modal) {
    modal.style.display = 'none';
    modal.classList.remove('open');
  }

  if (wasInteractive && pb?.close) {
    try {
      pb.close({ force: true });
    } catch (_) {
      /* ignore */
    }
  }

  recoverBuilderScrollLock();
  recoverStaleBuilderChrome();

  const message = captureOpenFailure(err) || 'Could not open editor. Please try again.';
  if (modal) {
    modal.style.display = 'block';
    modal.classList.add('open');
    document.body.classList.add('builder-open');
    const content = modal.querySelector('.pb-modal-content') || modal;
    const existing = modal.querySelector('[data-pb-cart-edit-error]');
    if (existing) existing.remove();
    const panel = document.createElement('div');
    panel.setAttribute('data-pb-cart-edit-error', '1');
    panel.setAttribute('role', 'alert');
    panel.style.cssText = 'padding:24px;text-align:center;';
    panel.innerHTML =
      '<p style="margin:0 0 16px;">' +
      String(message).replace(/</g, '&lt;').replace(/>/g, '&gt;') +
      '</p>' +
      '<button type="button" class="builder-btn" data-pb-cart-edit-error-close>Close</button>';
    content.appendChild(panel);
    panel.querySelector('[data-pb-cart-edit-error-close]')?.addEventListener('click', () => {
      panel.remove();
      modal.style.display = 'none';
      modal.classList.remove('open');
      recoverBuilderScrollLock();
      if (portal) {
        portal.hidden = true;
        portal.setAttribute('aria-hidden', 'true');
      }
    });
  } else {
    alert(message);
  }
}

function capChildLineEditMaxSteps(addonItem) {
  const s = window.builderState;
  const ctx = buildChildLineCartEditContext(s);
  if (!isChildLineCartEdit(ctx) || !addonItem) return;
  const max = getMaxChildLineSteps(ctx, addonItem);
  if (s.mode === 'SUB' && s.sub) {
    s.sub.maxSteps = max;
    if (s.sub.step > max) s.sub.step = max;
  }
}

function scrubStaleMediaPropertyBackups(pb, properties) {
  const out = { ...properties };
  const mainProduct = window.BUILDER_DATA?.mainProduct;
  collectAiImageMediaKeys(pb, mainProduct).forEach((key) => {
    if (!isMediaUrl(out[key])) return;
    delete out[`_${key}_json`];
    Object.keys(out).forEach((propKey) => {
      if (propKey.startsWith(`${key} (`) && propKey.endsWith(')')) delete out[propKey];
    });
  });
  return out;
}

function finalizeCartEditSaveProps(pb, properties, edit, fieldItem) {
  const product = fieldItem || window.BUILDER_DATA?.mainProduct;
  const fields = pb.getPersonalizationFields?.(product) || [];
  let out = properties;
  if (typeof pb.upgradeLegacyCartLinePropsOnSave === 'function') {
    out = pb.upgradeLegacyCartLinePropsOnSave(out, fields, {
      sourceProps: edit?.preservedProps,
    });
  }
  out = preserveExtraProperties(edit?.preservedProps, out);
  return scrubStaleMediaPropertyBackups(pb, out);
}

function preserveExtraProperties(props, newProps) {
  const out = { ...newProps };
  PRESERVED_CART_METADATA_KEYS.forEach((key) => {
    if (props?.[key] != null && out[key] == null) out[key] = props[key];
  });
  return out;
}

function hydrateBundleStateFromCart(cart, family, bundle, builderState, pb, options = {}) {
  if (!bundle || !family || !builderState) return;
  const bid = String(bundle.id);
  builderState.bundle = builderState.bundle || { selectedId: null, configurations: {} };
  builderState.bundle.selectedId = bid;
  builderState.bundle.configurations = builderState.bundle.configurations || {};
  builderState.bundle.configurations[bid] = builderState.bundle.configurations[bid] || {};

  const bundleLines = [family.mainLine, ...(family.bundleLines || [])].filter(Boolean);
  bundleLines.sort((a, b) => {
    const ai = resolveBundleRowIndexInCart(cart, a.key, family.bundleTitle);
    const bi = resolveBundleRowIndexInCart(cart, b.key, family.bundleTitle);
    return ai - bi;
  });

  bundleLines.forEach((line) => {
    const props = stripFamilySystemProperties(line.properties || {});
    builderState.bundle.configurations[bid][resolveBundleRowIndexInCart(cart, line.key, family.bundleTitle)] = {
      id: line.variant_id,
      properties: props,
    };
  });

  if (options.skipSeedAnchor) return;

  const mainProduct = window.BUILDER_DATA?.mainProduct;
  const firstMainIdx = (bundle.items || []).findIndex(
    (item) => String(item?.id) === String(mainProduct?.id)
  );
  if (firstMainIdx >= 0 && pb?.seedAnchorBundleRowFromMain) {
    pb.seedAnchorBundleRowFromMain(bid, bundle, mainProduct, builderState);
  }
}

/** Cart line is authoritative during edit — sync edited row into bundle slot (couple set). */
function syncCartEditBundleSlotFromLine(cart, item, family, bundle, builderState) {
  if (!cart || !item || !family?.bundleTitle || !bundle) return;
  const bid = String(bundle.id);
  const rowIdx = resolveBundleRowIndexInCart(cart, item.key, family.bundleTitle);
  if (rowIdx < 0) return;
  builderState.bundle = builderState.bundle || { selectedId: null, configurations: {} };
  builderState.bundle.selectedId = bid;
  builderState.bundle.configurations = builderState.bundle.configurations || {};
  if (!builderState.bundle.configurations[bid]) builderState.bundle.configurations[bid] = {};
  builderState.bundle.configurations[bid][rowIdx] = {
    id: item.variant_id,
    properties: stripFamilySystemProperties(item.properties || {}),
  };
}

/** Design library / AI only — never overwrite cart-line selections or bundle rows from stale session. */
function applyCartEditAuxiliaryPartial(state, partial) {
  if (!state || !partial) return;
  if (partial.catalogDesignLibrary) {
    applyCatalogDesignLibraryToState(state, partial.catalogDesignLibrary);
  }
}

function resolveOriginalOfferFromCart(item, builderData) {
  const bundleTitle = String(item?.properties?.['_Bundle'] || '').trim();
  if (!bundleTitle) {
    return { originalBundleId: null, originalBundleTitle: null };
  }
  const bundle = resolveBundleByTitle(bundleTitle, builderData);
  return {
    originalBundleId: bundle?.id != null ? String(bundle.id) : null,
    originalBundleTitle: bundleTitle,
  };
}

function getAddonTitleForCartKey(key, builderState, mainProduct) {
  const slot = builderState?.cartAddons?.[key];
  if (slot?.title) return slot.title;
  const parts = String(key || '').split('__');
  const addonProductId = parts.slice(1).join('__');
  const fromDb = addonProductId && builderState?.addonsDB?.[addonProductId];
  if (fromDb?.title) return fromDb.title;
  const fromMain = (mainProduct?.addons || []).find(
    (addon) => addon && String(addon.id) === String(addonProductId)
  );
  return fromMain?.title || '';
}

function getAddonStringForTargetIndex(builderState, targetIndex, mainProduct, pb) {
  const names = [];
  Object.keys(builderState?.cartAddons || {}).forEach((key) => {
    if (
      typeof pb?.isCartAddonKeyRedundantForBundle === 'function' &&
      pb.isCartAddonKeyRedundantForBundle(key)
    ) {
      return;
    }
    const parts = key.split('__');
    if (parseInt(parts[0], 10) !== targetIndex) return;
    const title = getAddonTitleForCartKey(key, builderState, mainProduct);
    if (title) names.push(title);
  });
  return names.join(', ');
}

function resolveAddonItemForCartAddonKey(key, builderState, mainProduct) {
  const parts = String(key || '').split('__');
  const addonProductId = parts.slice(1).join('__');
  if (!addonProductId) return null;
  if (builderState?.addonsDB?.[addonProductId]) return builderState.addonsDB[addonProductId];
  const fromMain = (mainProduct?.addons || []).find(
    (addon) => addon && String(addon.id) === String(addonProductId)
  );
  if (fromMain) return fromMain;
  const selectedId = builderState?.bundle?.selectedId;
  if (!selectedId) return null;
  const bundle = (window.BUILDER_DATA?.bundles || []).find(
    (row) => String(row?.id) === String(selectedId)
  );
  if (!bundle?.items) return null;
  for (let i = 0; i < bundle.items.length; i += 1) {
    const hit = (bundle.items[i]?.addons || []).find(
      (addon) => addon && String(addon.id) === String(addonProductId)
    );
    if (hit) return hit;
  }
  return null;
}

function buildDesiredCartItemsForMainEdit(pb, builderState, edit) {
  const s = builderState;
  const items = [];
  const builderData = window.BUILDER_DATA;
  const mainProduct = builderData?.mainProduct;
  const inherit = getShopifyBundleInherit();
  const quantity = edit?.quantity ?? 1;

  if (
    isShopifyBundleCartEditContext(builderData) &&
    inherit?.buildShopifyBundleCartEditMainItem
  ) {
    const mainAddons = getAddonStringForTargetIndex(s, 0, mainProduct, pb);
    const mainItem = inherit.buildShopifyBundleCartEditMainItem({
      pb,
      builderState: s,
      builderData,
      edit,
      addonString: mainAddons || '',
    });
    if (mainItem?.id) items.push(mainItem);

    Object.keys(s.cartAddons || {}).forEach((key) => {
      const data = s.cartAddons[key];
      if (!data?.id) return;
      if (
        typeof pb?.isCartAddonKeyRedundantForBundle === 'function' &&
        pb.isCartAddonKeyRedundantForBundle(key)
      ) {
        return;
      }
      const addonItem = resolveAddonItemForCartAddonKey(key, s, mainProduct);
      const props =
        typeof pb.encodeCartLineFromStoredProps === 'function'
          ? pb.encodeCartLineFromStoredProps(data.properties || {}, addonItem?.options || [], {
              extra: { 'For Item': mainProduct?.title || '' },
              variantId: data.id,
              fieldItem: addonItem,
            })
          : {
              ...stripSystemProperties(data.properties || {}),
              'For Item': mainProduct?.title || '',
            };
      props._pb_parent_row = String(parseInt(key.split('__')[0], 10));
      items.push({ id: data.id, quantity: 1, properties: props });
    });

    return items.filter((row) => row?.id);
  }

  const mainVariant = resolveMainVariantFromState(s);
  if (!mainVariant?.id || !mainProduct) return items;

  const sameId = (a, b) => String(a?.id) === String(b?.id);
  const offerSwitched = isOfferSwitch(edit?.originalBundleId, s.bundle?.selectedId);
  const cartAddons = offerSwitched ? {} : s.cartAddons || {};

  const mainStateSlice = {
    selections: s.main?.selections || {},
    topicSelections: s.main?.topicSelections || {},
    variantId: mainVariant.id,
    catalogDesignLibrary: s.catalogDesignLibrary,
  };

  if (!s.bundle?.selectedId) {
    const mainAddons = getAddonStringForTargetIndex(s, 0, mainProduct, pb);
    const extra = mainAddons ? { 'Add-ons': mainAddons } : {};
    const mainProps = pb.encodeCartLineProperties
      ? pb.encodeCartLineProperties(mainStateSlice, s.main?.options || [], extra)
      : {};
    items.push({
      id: mainVariant.id,
      quantity,
      properties: mainProps,
    });
  } else {
    const selBid = String(s.bundle.selectedId);
    const bundle = (window.BUILDER_DATA?.bundles || []).find(
      (row) => String(row?.id) === selBid
    );
    if (!bundle?.items?.length) {
      const mainAddons = getAddonStringForTargetIndex(s, 0, mainProduct, pb);
      const extra = mainAddons ? { 'Add-ons': mainAddons } : {};
      const mainProps = pb.encodeCartLineProperties
        ? pb.encodeCartLineProperties(mainStateSlice, s.main?.options || [], extra)
        : {};
      items.push({ id: mainVariant.id, quantity, properties: mainProps });
    } else {
      const firstMainIdx = bundle.items.findIndex((item) => sameId(item, mainProduct));
      bundle.items.forEach((item, idx) => {
        if (typeof pb.encodeBundleCartLineItem !== 'function') return;
        const rowAddons = getAddonStringForTargetIndex(s, idx, mainProduct, pb);
        const extraBase = rowAddons ? { 'Add-ons': rowAddons } : {};
        const rowItem = pb.encodeBundleCartLineItem(
          selBid,
          idx,
          item,
          bundle,
          firstMainIdx,
          mainProduct,
          s,
          mainVariant,
          extraBase
        );
        if (rowItem?.id) {
          items.push({
            id: rowItem.id,
            quantity: rowItem.quantity ?? 1,
            properties: rowItem.properties || {},
          });
        }
      });
    }
  }

  Object.keys(cartAddons).forEach((key) => {
    const data = cartAddons[key];
    if (!data?.id) return;
    if (
      typeof pb?.isCartAddonKeyRedundantForBundle === 'function' &&
      pb.isCartAddonKeyRedundantForBundle(key)
    ) {
      return;
    }
    const parts = key.split('__');
    const targetIndex = parseInt(parts[0], 10);
    let targetName = mainProduct.title;
    if (!s.bundle?.selectedId) {
      targetName = mainProduct.title;
    } else {
      const bundle = (window.BUILDER_DATA?.bundles || []).find(
        (row) => String(row?.id) === String(s.bundle.selectedId)
      );
      if (bundle?.items?.[targetIndex]) targetName = bundle.items[targetIndex].title;
    }
    const addonItem = resolveAddonItemForCartAddonKey(key, s, mainProduct);
    const props =
      typeof pb.encodeCartLineFromStoredProps === 'function'
        ? pb.encodeCartLineFromStoredProps(data.properties || {}, addonItem?.options || [], {
            extra: { 'For Item': targetName },
            variantId: data.id,
            fieldItem: addonItem,
          })
        : {
            ...stripSystemProperties(data.properties || {}),
            'For Item': targetName,
          };
    props._pb_parent_row = String(targetIndex);
    items.push({ id: data.id, quantity: 1, properties: props });
  });

  return items.filter((row) => row?.id);
}


function buildAddonItemFromMainProduct(mainProduct) {
  if (!mainProduct) return null;
  const pb = window.productBuilder;
  const data = window.BUILDER_DATA || {};
  let host;
  if (typeof pb?.getMainProductHost === 'function') {
    host = pb.getMainProductHost(mainProduct, window.builderState);
  } else {
    host = {
      id: String(mainProduct.id),
      handle: mainProduct.handle,
      title: mainProduct.title,
      price: mainProduct.price,
      image: mainProduct.image,
      images: mainProduct.images || [],
      variants: mainProduct.variants || data.variants || [],
      options: mainProduct.options || data.options || data.optionsWithValues || [],
      config: mainProduct.config || mainProduct.personalization || [],
      isSimple: mainProduct.isSimple,
    };
  }
  if (!host) return null;
  return {
    ...host,
    id: String(host.id ?? mainProduct.id),
    config: host.config || mainProduct.config || mainProduct.personalization || [],
    skipPersonalizationInBundle: false,
  };
}

function resolveAddonItemForCartLine(cartLine, builderData, builderState) {
  const data = builderData || window.BUILDER_DATA || {};
  const state = builderState || window.builderState || {};
  const productId = cartLine?.product_id != null ? String(cartLine.product_id) : '';
  if (!productId) return null;

  const db = state.addonsDB || {};
  if (db[productId]) {
    return { ...db[productId], skipPersonalizationInBundle: false };
  }

  const fromList = (addons) =>
    (Array.isArray(addons) ? addons : []).find((a) => a && String(a.id) === productId);

  const mainHit = fromList(data.mainProduct?.addons);
  if (mainHit) return { ...mainHit, skipPersonalizationInBundle: false };

  for (const bundle of data.bundles || []) {
    for (const line of bundle?.items || []) {
      const hit = fromList(line?.addons);
      if (hit) return { ...hit, skipPersonalizationInBundle: false };
    }
  }

  if (data.mainProduct && String(data.mainProduct.id) === productId) {
    return buildAddonItemFromMainProduct(data.mainProduct);
  }

  return null;
}

function ensureFieldRuntimeForCartEdit(pb) {
  if (typeof globalThis.__pbEnsureFieldRuntimeInstalled === 'function') {
    return globalThis.__pbEnsureFieldRuntimeInstalled(pb);
  }
  return typeof pb?.getFieldRegistry === 'function';
}

function isChildLineCartEditSession(edit) {
  return !!(edit?.isAddon || edit?.isBundleRow);
}

function resolveChildLineCatalogItem(builderState, edit, cart) {
  const s = builderState;
  if (edit?.isAddon) {
    return (
      s?.addonsDB?.[s.sub?.currentId] ||
      resolveAddonItemForCartLine({ product_id: edit.productId }, window.BUILDER_DATA, s)
    );
  }
  if (edit?.isBundleRow) {
    const line = (cart?.items || []).find((row) => row.key === edit.lineKey);
    return line ? resolveBundleRowCatalogItem(line, cart, window.BUILDER_DATA) : null;
  }
  return null;
}

function expandChildLineSelections(pb, item, catalogItem) {
  const props = item?.properties || {};
  const collapsed = pb.collapseNumberedProperties ? pb.collapseNumberedProperties(props) : props;
  const expanded = pb.expandDelimiterProperties ? pb.expandDelimiterProperties(collapsed) : collapsed;
  let selections = pb.nestBundleSelectionsForAiImageFields
    ? pb.nestBundleSelectionsForAiImageFields(catalogItem, { ...expanded })
    : { ...expanded };
  const dlLogic = window.PBDesignLibraryLogic;
  if (dlLogic?.mergeDesignLibraryCartPropsIntoSelections) {
    selections = dlLogic.mergeDesignLibraryCartPropsIntoSelections(props, selections);
  }
  applyVariantToSelections(selections, catalogItem?.options, findVariantById(catalogItem?.variants, item?.variant_id));
  return selections;
}

function resolveChildLineVariantId(pb, catalogItem, subSlice, edit) {
  if (catalogItem?.variants?.length && typeof pb.resolveBundleItemVariantId === 'function') {
    const fromSelections = pb.resolveBundleItemVariantId(catalogItem, subSlice?.selections || {});
    if (fromSelections) return fromSelections;
  }
  if (subSlice?.variantId != null) return subSlice.variantId;
  if (edit?.variantId != null) return edit.variantId;
  return catalogItem?.variants?.[0]?.id || null;
}

async function waitForSubFlowDom() {
  const deadline = Date.now() + 3000;
  return new Promise((resolve) => {
    function tick() {
      const body = document.getElementById('sub-builder-body');
      if (body?.querySelector('.pb-step') || Date.now() > deadline) {
        resolve();
        return;
      }
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(tick);
      else setTimeout(tick, 16);
    }
    tick();
  });
}

function prepareAddonCartEditState(pb, item) {
  const addonItem = resolveAddonItemForCartLine(
    item,
    window.BUILDER_DATA,
    window.builderState
  );
  if (!addonItem) throw new Error('Add-on product could not load.');

  const s = window.builderState;
  delete s._addonCartSubPresented;
  delete s._pendingAddonCartPresent;

  s.addonsDB = s.addonsDB || {};
  s.addonsDB[String(addonItem.id)] = addonItem;

  const selections = expandChildLineSelections(pb, item, addonItem);
  const fields = pb.getPersonalizationFields?.(addonItem) || [];
  const topicSelections = inferTopicSelectionsForEdit(
    pb,
    selections,
    pb.parseTopicSelectionsBackup?.(item.properties || {}) || {},
    fields,
    item.properties || {}
  );

  s.cartEdit = {
    lineKey: item.key,
    quantity: item.quantity,
    variantId: item.variant_id,
    productId: item.product_id,
    preservedProps: expandCartLinePropsForEdit(pb, item.properties || {}),
    hydrationSource: 'line',
    isAddon: true,
    forItem: item.properties['For Item'],
  };

  s.sub.forceFullPersonalization = true;
  s.sub._fieldFlow = 'cart_edit';
  s.sub.currentId = String(addonItem.id);
  s.sub.isQueueItem = false;
  s.sub.tempUniqueKey = null;
  s.sub.variantId = item.variant_id;
  s.sub.selections = selections;
  s.sub.topicSelections = topicSelections;
  s.sub.touched = {};
  Object.keys(item.properties || {}).forEach((key) => {
    if (key && key.charAt(0) !== '_') s.sub.touched[key] = true;
  });
  s._pendingAddonCartPresent = true;

  applyEntryManifest(
    s,
    createSubAddonCartEditEntryManifest({
      lineKey: item.key,
      variantId: item.variant_id,
      hydrationSource: 'line',
    }),
    s.cartEdit
  );
}

function hydrateBundleRowDesignLibraryForCartEdit(s, rowItem, cartProps, pb) {
  if (!s?.sub || !rowItem || !window.PBBundleRowEncode?.prepareBundleRowSubContext) return;
  const expanded = expandCartLinePropsForEdit(pb, cartProps || {});
  window.PBBundleRowEncode.prepareBundleRowSubContext(s, rowItem, expanded, pb);
  if (s.sub.catalogDesignLibrary?.byLabel) {
    s.sub.catalogDesignLibrary = enrichCatalogDesignLibraryFromTier1Cart(
      s.sub.catalogDesignLibrary,
      cartProps || {},
      window.BUILDER_DATA?.designLibraryIndex || []
    );
  }
}

function prepareBundleRowCartEditState(pb, item, cart) {
  const rowItem = resolveBundleRowCatalogItem(item, cart, window.BUILDER_DATA);
  if (!rowItem) throw new Error('Bundle row could not load.');

  const bundleTitle = String(item.properties?.['_Bundle'] || '').trim();
  const bundle = resolveBundleByTitle(bundleTitle, window.BUILDER_DATA);
  const rowIndex = rowItem._bundleRowIndex ?? -1;

  const s = window.builderState;
  const cartProps = item.properties || {};
  const selections = expandChildLineSelections(pb, item, rowItem);
  const fields = pb.getPersonalizationFields?.(rowItem) || [];
  const topicSelections = inferTopicSelectionsForEdit(
    pb,
    selections,
    pb.parseTopicSelectionsBackup?.(cartProps) || {},
    fields,
    cartProps
  );

  s.cartEdit = {
    lineKey: item.key,
    quantity: item.quantity,
    variantId: item.variant_id,
    productId: item.product_id,
    preservedProps: expandCartLinePropsForEdit(pb, cartProps),
    hydrationSource: 'line',
    isBundleRow: true,
    bundleTitle,
    bundleId: bundle?.id != null ? String(bundle.id) : null,
    bundleRowIndex: rowIndex,
    familyId: resolveFamilyAnchorLineKey(cart, item.key),
  };

  if (bundle?.id) {
    s.bundle = s.bundle || { selectedId: null, configurations: {} };
    s.bundle.selectedId = String(bundle.id);
  }

  s.sub.forceFullPersonalization = true;
  s.sub._fieldFlow = 'cart_edit';
  s.sub.currentId = String(rowIndex);
  s.sub.isQueueItem = true;
  s.sub.tempUniqueKey = null;
  s.sub.variantId = item.variant_id;
  s.sub.selections = selections;
  s.sub.topicSelections = topicSelections;
  s.sub.touched = {};
  Object.keys(cartProps).forEach((key) => {
    if (key && key.charAt(0) !== '_') s.sub.touched[key] = true;
  });
  hydrateBundleRowDesignLibraryForCartEdit(s, rowItem, cartProps, pb);
  s._pendingAddonCartPresent = true;

  applyEntryManifest(
    s,
    createSubBundleCartEditEntryManifest({
      lineKey: item.key,
      bundleRowIndex: rowIndex,
      variantId: item.variant_id,
      hydrationSource: 'line',
    }),
    s.cartEdit
  );
}

async function enterChildLineCartEditSubFlow(pb) {
  const s = window.builderState;
  const edit = s?.cartEdit;
  if (!isChildLineCartEditSession(edit)) return;
  if (s._addonCartSubPresented) return;

  await ensureModulesReadyForEntryManifest(readEntryManifest(s));
  ensureFieldRuntimeForCartEdit(pb);

  const catalogItem = resolveChildLineCatalogItem(s, edit, s._pbCartEditCart);
  if (!catalogItem) {
    throw new Error(edit?.isBundleRow ? 'Bundle row could not load.' : 'Add-on product could not load.');
  }

  const preservedTouched = { ...(s.sub.touched || {}) };
  const preservedSelections = { ...(s.sub.selections || {}) };
  const preservedTopics = { ...(s.sub.topicSelections || {}) };

  s.mode = 'SUB';
  const manifest = readEntryManifest(s);
  s.sub.touched = preservedTouched;
  s.sub.selections = preservedSelections;
  s.sub.topicSelections = preservedTopics;
  s.sub.forceFullPersonalization = true;
  s.sub._fieldFlow = CART_EDIT_FLOW;
  s.sub.variants = catalogItem.variants || [];
  s.sub.options = catalogItem.options || [];
  s.sub.options.forEach((option) => {
    if (option.name && s.sub.selections[option.name]) s.sub.touched[option.name] = true;
  });
  s.sub.activeImages = catalogItem.images || [];

  if (edit.isBundleRow) {
    s.sub.isQueueItem = true;
    s.sub.currentId = String(edit.bundleRowIndex ?? catalogItem._bundleRowIndex ?? 0);
  } else {
    s.sub.isQueueItem = false;
    s.sub.currentId = String(catalogItem.id);
  }
  s.sub.variantId = edit.variantId;

  // Restore the edited line before fields mount: activation may immediately prefetch AI text.
  const aiTextDraft = resolveCartEditAiTextDraft(
    { properties: edit.preservedProps }, catalogItem, null, s.sub.selections
  );
  applyAiTextToState(s, aiTextDraft, pb.getPersonalizationFields?.(catalogItem) || catalogItem.config || []);

  await waitForSubFlowDom();
  const presenter = createCartEditPresenter({ pb, mode: 'SUB' });
  await presenter.present(manifest, catalogItem);
  capChildLineEditMaxSteps(catalogItem);
  hydrateChildLineCartEditMedia({ forceAiReinit: true });

  delete s._pendingAddonCartPresent;
  s._addonCartSubPresented = true;
}

async function enterAddonCartEditSubFlow(pb) {
  return enterChildLineCartEditSubFlow(pb);
}

async function stabilizeChildLineCartEditSubFlow(pb) {
  const s = window.builderState;
  const edit = s?.cartEdit;
  if (!isChildLineCartEditSession(edit)) return;

  await ensureModulesReadyForEntryManifest(readEntryManifest(s));
  ensureFieldRuntimeForCartEdit(pb);

  const catalogItem = resolveChildLineCatalogItem(s, edit, s._pbCartEditCart);
  if (!catalogItem) {
    throw new Error(edit?.isBundleRow ? 'Bundle row could not load.' : 'Add-on product could not load.');
  }

  s.mode = 'SUB';
  s.sub.forceFullPersonalization = true;
  if (edit.isBundleRow) {
    s.sub.isQueueItem = true;
    s.sub.currentId = String(edit.bundleRowIndex ?? catalogItem._bundleRowIndex ?? 0);
  }

  const mainCont = document.getElementById('main-flow-container');
  const subCont = document.getElementById('sub-flow-container');
  if (mainCont) mainCont.style.display = 'none';
  if (subCont) subCont.style.display = 'flex';

  const presenter = createCartEditPresenter({ pb, mode: 'SUB' });
  const manifest = readEntryManifest(s);
  await presenter.present(manifest, catalogItem);
  capChildLineEditMaxSteps(catalogItem);
  hydrateChildLineCartEditMedia({ forceAiReinit: true });

  delete s._pendingAddonCartPresent;
  s._addonCartSubPresented = true;
}

async function stabilizeAddonCartEditSubFlow(pb) {
  return stabilizeChildLineCartEditSubFlow(pb);
}

function resolveMainVariantFromState(s) {
  const variants = s?.main?.variants || window.BUILDER_DATA?.variants || [];
  const options = s?.main?.options || [];
  const selections = s?.main?.selections || {};
  return (
    variants.find((v) => options.every((opt, idx) => v.options[idx] === selections[opt.name])) ||
    variants.find((v) => v.available) ||
    variants[0] ||
    null
  );
}

function saveMainCartEdit() {
  const pb = window.productBuilder;
  const s = window.builderState;
  const edit = s?.cartEdit;
  if (!edit || isChildLineCartEditSession(edit) || !pb) return;

  if (!ensureFinalizeHookInstalled()) {
    alert('Cart editor is still loading. Please wait a moment and try again.');
    return;
  }

  flushCartEditAiImageFromDom();
  const mainProduct = window.BUILDER_DATA?.mainProduct;
  if (mainProduct && typeof pb.normalizeMainAiImageSelections === 'function') {
    pb.normalizeMainAiImageSelections(mainProduct);
  }
  flushCartEditAiImageFromDom();
  pb.flushFlowFieldsToState?.('#main-flow-container');

  if (
    typeof window.PBFinalizeGate?.attempt === 'function' &&
    !window.PBFinalizeGate.attempt(pb, s.main)
  ) {
    return;
  }

  const mainVariant = resolveMainVariantFromState(s);
  if (!mainVariant?.id) {
    alert('Could not determine product variant.');
    return;
  }

  const btn = document.getElementById('nextBtn');
  const originalText = btn?.innerText || 'Update cart';
  return pb.finalizeCartEdit([{ id: mainVariant.id }], btn, originalText);
}

function saveChildLineCartEdit() {
  const pb = window.productBuilder;
  const s = window.builderState;
  const edit = s?.cartEdit;
  if (!isChildLineCartEditSession(edit) || !pb) return;

  if (!ensureFinalizeHookInstalled()) {
    alert('Cart editor is still loading. Please wait a moment and try again.');
    return;
  }

  flushCartEditAiImageFromDom();
  pb.flushFlowFieldsToState?.('#sub-flow-container');
  const catalogItem = resolveChildLineCatalogItem(s, edit, s._pbCartEditCart);
  if (!catalogItem) {
    alert(edit?.isBundleRow ? 'Bundle row configuration could not be loaded.' : 'Add-on configuration could not be loaded.');
    return;
  }

  if (typeof pb.normalizeMainAiImageSelections === 'function') {
    pb.normalizeMainAiImageSelections(catalogItem);
  }
  flushCartEditAiImageFromDom();

  if (
    typeof window.PBFinalizeGate?.attempt === 'function' &&
    !window.PBFinalizeGate.attempt(pb, s.sub)
  ) {
    return;
  }

  const variantId = resolveChildLineVariantId(pb, catalogItem, s.sub, edit);
  if (!variantId) {
    alert(edit?.isBundleRow ? 'Could not determine bundle row variant.' : 'Could not determine add-on variant.');
    return;
  }

  const btn = document.getElementById('subNextBtn');
  const originalText = btn?.innerText || 'Update cart';
  return pb.finalizeCartEdit([{ id: variantId }], btn, originalText);
}

function saveAddonCartEdit() {
  return saveChildLineCartEdit();
}

function encodeAddonLineForFamilySave(pb, slot, addonItem, forItemTitle) {
  if (typeof pb?.encodeCartLineFromStoredProps === 'function') {
    return pb.encodeCartLineFromStoredProps(slot.properties || {}, addonItem?.options || [], {
      extra: {
        'For Item': forItemTitle,
        'Add-ons': addonItem.title || slot.title || '',
      },
      variantId: slot.id,
      fieldItem: addonItem,
    });
  }
  return {
    ...stripSystemProperties(slot.properties),
    'For Item': forItemTitle,
    'Add-ons': addonItem.title || slot.title || '',
  };
}

function encodeMainCartEditPropertiesWithAddons(pb, newItem, edit, builderState) {
  const ctx = builderState?.main;
  const builderData = window.BUILDER_DATA;
  const mainProduct = builderData?.mainProduct;
  const inherit = getShopifyBundleInherit();

  if (
    isShopifyBundleCartEditContext(builderData) &&
    inherit?.encodeShopifyBundleCartEditProperties
  ) {
    const mainAddons = getAddonStringForTargetIndex(builderState, 0, mainProduct, pb);
    const properties = inherit.encodeShopifyBundleCartEditProperties({
      pb,
      builderState,
      builderData,
      addonString: mainAddons || '',
    });
    if (properties) {
      return finalizeCartEditSaveProps(pb, properties, edit, mainProduct);
    }
  }

  const options = ctx?.options || mainProduct?.options || [];
  const addonsMeta = buildAddonsMetadataString(builderState?.cartAddons, window.BUILDER_DATA);
  const extra = addonsMeta ? { 'Add-ons': addonsMeta } : {};

  let properties;
  if (pb?.encodeCartLineProperties) {
    properties = pb.encodeCartLineProperties(
      {
        selections: ctx?.selections || {},
        topicSelections: ctx?.topicSelections || {},
        catalogDesignLibrary: builderState?.catalogDesignLibrary,
        variantId: newItem.id,
      },
      options,
      extra
    );
  } else {
    properties = encodeCartEditProperties(pb, newItem, edit);
  }

  return finalizeCartEditSaveProps(pb, properties, edit, mainProduct);
}

function hydrateMainLineAddonsFromCart(cart, item, builderState) {
  const family = discoverFamily(cart, item.key);
  if (!family?.addonLines?.length) return;
  const hydrated = hydrateAddonSelectionsFromFamily(
    family,
    window.BUILDER_DATA,
    builderState,
    { resolveAddonItemForCartLine }
  );
  builderState.cartAddons = hydrated.cartAddons;
  builderState.addonsDB = { ...(builderState.addonsDB || {}), ...hydrated.addonsDB };
}

function encodeCartEditProperties(pb, newItem, edit) {
  assertEncodeBeltReady(pb, 'encodeCartEditProperties');

  if (isChildLineCartEditSession(edit)) {
    const catalogItem = resolveChildLineCatalogItem(window.builderState, edit, window.builderState?._pbCartEditCart);
    pb.flushFlowFieldsToState?.('#sub-flow-container');
    let properties;
    if (
      edit.isBundleRow &&
      window.PBBundleRowEncode &&
      typeof window.PBBundleRowEncode.encodeManualBundleRowSave === 'function' &&
      catalogItem
    ) {
      properties = window.PBBundleRowEncode.encodeManualBundleRowSave({
        pb,
        item: catalogItem,
        state: window.builderState,
        sourceProps: edit.preservedProps,
      });
    } else if (typeof pb.encodeFlowStateForCart === 'function' && catalogItem) {
      properties = pb.encodeFlowStateForCart(
        window.builderState?.sub,
        catalogItem,
        window.builderState?.sub?.options || []
      );
    } else {
      const err = new Error('Personalization could not be saved. Please refresh the page and try again.');
      err.code = 'PB_ENCODE_BELT_NOT_READY';
      err.context = 'encodeCartEditProperties:child-line';
      throw err;
    }
    return finalizeCartEditSaveProps(pb, properties, edit, catalogItem);
  }

  const ctx = window.builderState?.main;
  const mainProduct = window.BUILDER_DATA?.mainProduct;
  const options = ctx?.options || mainProduct?.options || [];
  const stateSlice = {
    selections: ctx?.selections || {},
    topicSelections: ctx?.topicSelections || {},
    catalogDesignLibrary: window.builderState?.catalogDesignLibrary,
    variantId: newItem.id,
  };

  let properties;
  if (pb?.encodeCartLineProperties) {
    properties = pb.encodeCartLineProperties(stateSlice, options, {});
  } else if (pb?.encodeBuilderStateForCart) {
    const fields = pb.getPersonalizationFields?.(mainProduct) || [];
    properties = pb.encodeBuilderStateForCart(stateSlice, fields, options);
  } else {
    const err = new Error('Personalization could not be saved. Please refresh the page and try again.');
    err.code = 'PB_ENCODE_BELT_NOT_READY';
    err.context = 'encodeCartEditProperties:main-line';
    throw err;
  }

  return finalizeCartEditSaveProps(pb, properties, edit, mainProduct);
}

function findCartLine(cart, lineKey) {
  if (!cart?.items?.length || !lineKey) return null;
  return cart.items.find((row) => row.key === lineKey) || null;
}

async function resolveCartLine(lineKey) {
  const cart = await fetchCart();
  const row = findCartLine(cart, lineKey);
  if (!row) {
    throw new Error('This cart item could no longer be found. Please refresh the cart and try again.');
  }
  return row;
}

/** Shopify `/cart/change.js` accepts the line item key via `id` (not variant id). */
function buildCartLineChangePayload(lineKey, quantity, properties, sectionsPayload) {
  const payload = { id: lineKey, quantity, ...sectionsPayload };
  if (properties != null) payload.properties = properties;
  return payload;
}

function assertCartResponse(parsed, fallbackMessage) {
  if (!parsed || typeof parsed !== 'object') {
    throw new Error(fallbackMessage);
  }
  if (parsed.errors) {
    throw new Error(typeof parsed.errors === 'string' ? parsed.errors : fallbackMessage);
  }
  if (parsed.status && parsed.description) {
    throw new Error(parsed.description);
  }
}

async function postCartChange(payload) {
  const body = JSON.stringify(payload);
  const res = await fetch(getThemeRoutes().cart_change_url, fetchConfig('json', { body }));
  return readCartWriteResponse(res, 'Cart update failed');
}

async function postCartAdd(payload) {
  const body = JSON.stringify(payload);
  const res = await fetch(getThemeRoutes().cart_add_url, fetchConfig('json', { body }));
  return readCartWriteResponse(res, 'Could not add updated item to cart');
}

async function readCartWriteResponse(response, fallback) {
  const parsed = await response.json();
  try {
    assertCartResponse(parsed, fallback);
    if (!response.ok) throw new Error(fallback);
  } catch (error) {
    error.cartWriteRejected = response.status >= 400 && response.status < 500 && response.status !== 408;
    throw error;
  }
  return parsed;
}

/** Fetch section HTML only when a lost response left us without current cart markup. */
async function refreshCartAfterSave(cart) {
  const ids = getCartSectionIds();
  if (ids.some(id => !cart.sections?.[id])) {
    const url = new URL(window.location.href);
    url.search = '';
    url.searchParams.set('sections', ids.join(','));
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('Your cart is saved, but its display could not refresh. Retry to finish.');
    cart.sections = await response.json();
    if (ids.some(id => !cart.sections?.[id])) throw new Error('Your cart display is still loading. Retry to finish.');
  }
  refreshCartSections(cart);
}

/** Tear down cart edit session and fully dismiss the builder modal (returns user to cart drawer). */
function completeCartEditSession() {
  const s = window.builderState;
  if (s) {
    s.cartEdit = null;
    s.mode = 'MAIN';
    delete s._addonCartSubPresented;
    delete s._pendingAddonCartPresent;
    delete s.aiImageByLabel;
    if (s.sub) {
      s.sub.step = 1;
      s.sub.selections = {};
      s.sub.topicSelections = {};
      s.sub.touched = {};
      s.sub.currentId = null;
      s.sub.variantId = null;
      s.sub.isQueueItem = false;
      s.sub.tempUniqueKey = null;
      delete s.sub.aiImageByLabel;
      delete s.sub.catalogDesignLibrary;
    }
  }
  const pb = window.productBuilder;
  if (pb?.close) pb.close({ force: true });
}

function installCartEditHelpers(pb) {
  if (!pb || pb._pbCartEditHelpersInstalled) return;
  pb._pbCartEditHelpersInstalled = true;
  pb.expandCartLinePropsForEdit = (props) => expandCartLinePropsForEdit(pb, props);
  pb.flushCartEditAiImageFromDom = flushCartEditAiImageFromDom;
}

function registerCartEditFinalizeHelpers() {
  if (typeof window === 'undefined') return;
  window.__pbCartEditFinalizeHelpers = {
    fetchCart,
    resolveCartLine,
    postCartChange,
    postCartAdd,
    refreshCartSections,
    refreshCartAfterSave,
    getCartSectionIds,
    buildCartLineChangePayload,
    encodeCartEditProperties,
    encodeMainCartEditPropertiesWithAddons,
    buildDesiredCartItemsForMainEdit,
    encodeAddonLineForFamilySave,
    resolveAddonItemForCartLine,
    completeCartEditSession,
  };
}

function installFinalizeHook() {
  if (!window.productBuilder || window.productBuilder._pbCartEditInstalled) {
    return !!window.productBuilder?._pbCartEditInstalled;
  }
  window.productBuilder._pbCartEditInstalled = true;
  installCartEditHelpers(window.productBuilder);
  registerCartEditFinalizeHelpers();
  window.productBuilder.saveMainCartEdit = saveMainCartEdit;
  window.productBuilder.saveAddonCartEdit = saveAddonCartEdit;
  window.productBuilder.saveChildLineCartEdit = saveChildLineCartEdit;

  window.productBuilder.finalizeCartEdit = async function finalizeCartEdit(items, btn, originalText) {
    const presenter = createCartEditPresenter({ pb: window.productBuilder });
    return presenter.finalize(items, btn, originalText).catch(() => false);
  };
  return true;
}

function ensureFinalizeHookInstalled() {
  if (installFinalizeHook()) return true;
  if (!ensureFinalizeHookInstalled._bootListener) {
    ensureFinalizeHookInstalled._bootListener = true;
    document.addEventListener('pb:boot-ready', () => installFinalizeHook(), { once: true });
  }
  return false;
}

/** Present SUB child-line cart edit after modal is open — deferred from beforePresent. */
export async function presentPendingChildLineCartEdit(pb) {
  const s = window.builderState;
  if (!s?._pendingAddonCartPresent || s._addonCartSubPresented) return;
  await enterChildLineCartEditSubFlow(pb);
}

export async function restorePendingCartSave({ refresh = false } = {}) {
  registerCartEditFinalizeHelpers();
  const { restoreCartSaveRecovery } = await import('@theme/pb-cart-edit-finalize');
  const pending = await restoreCartSaveRecovery(window.__pbCartEditFinalizeHelpers);
  if (!pending && refresh) await refreshCartAfterSave(await fetchCart());
  return pending;
}

export async function openCartEdit(btn) {
  if (await restorePendingCartSave()) return;
  const lineKey = btn.dataset.lineKey;
  const handle = btn.dataset.productHandle;
  const productId = btn.dataset.productId;
  const anchorHandleAttr = btn.dataset.anchorHandle;
  const anchorProductIdAttr = btn.dataset.anchorProductId;
  if (!lineKey || !handle || !productId) {
    console.warn('[pb-cart-edit] Missing line metadata on Edit button');
    return;
  }

  const label = btn.textContent;
  btn.disabled = true;
  btn.setAttribute('aria-busy', 'true');
  btn.textContent = 'Loading...';

  let cartEditOpenStarted = false;
  try {
    const cart = await fetchCart();
    const item = (cart.items || []).find((row) => row.key === lineKey);
    if (!item || String(item.product_id) !== String(productId) || !isEditableCartLine(item)) {
      throw new Error('This cart item can no longer be edited.');
    }

    const childLineEdit = isChildLineCartEditTarget(cart, item);
    let builderHandle = handle;
    let builderProductId = productId;

    if (childLineEdit) {
      const anchorFromButton =
        anchorHandleAttr && anchorProductIdAttr
          ? { handle: anchorHandleAttr, productId: String(anchorProductIdAttr) }
          : null;
      const anchorMount = anchorFromButton || resolveAnchorProductMount(cart, item);
      if (!anchorMount?.handle || !anchorMount?.productId) {
        throw new Error('Could not find the main product for this cart item.');
      }
      builderHandle = anchorMount.handle;
      builderProductId = anchorMount.productId;
    }

    installCloseResumeHook();
    cartEditOpenStarted = true;

    await bootBuilderOpen({
      entry: 'cartEdit',
      handle: builderHandle,
      productId: builderProductId,
      suspendCartDrawer: true,
      beforePresent: async (pb) => {
        // builderState must be bound before any readEntryManifest(s) — TDZ regression guard
        const s = window.builderState;
        resetCartEditOpenState();
        ensureFinalizeHookInstalled();
        await ensureModulesReadyForEntryManifest(readEntryManifest(s));
        await ensureEncodeBeltModulesReady();

        installCartEditHelpers(pb);

        s._pbCartEditCart = cart;
        assertLegacyBundleUnambiguous(cart, item, window.BUILDER_DATA);
        const owner = isChildLineCartEditTarget(cart, item)
          ? resolveAnchorCartLineForChildEdit(cart, item) : null;
        const openedSources = snapshotFamilySources(discoverFamily(cart, owner?.key || item.key));

        const inherit = getShopifyBundleInherit();
        if (inherit?.prepareShopifyBundleCartEditState) {
          inherit.prepareShopifyBundleCartEditState(window.BUILDER_DATA, s);
        }

        if (isAddonCartLine(item)) {
          prepareAddonCartEditState(pb, item);
          s.cartEdit.sourceLines = openedSources;
          return;
        }

        if (isBundleChildCartLine(cart, item.key)) {
          prepareBundleRowCartEditState(pb, item, cart);
          s.cartEdit.sourceLines = openedSources;
          return;
        }

        const { hydrated, partial, source } = resolveCartEditHydration(pb, item);
        const family = discoverFamily(cart, item.key);
        const originalOffer = resolveOriginalOfferFromCart(item, window.BUILDER_DATA);

        s.mode = 'MAIN';
        s.main._fieldFlow = CART_EDIT_FLOW;
        s.bundle = s.bundle || { selectedId: null, configurations: {} };
        s.bundle.configurations = s.bundle.configurations || {};

        s.cartEdit = {
          sourceLines: openedSources,
          lineKey: item.key,
          quantity: item.quantity,
          variantId: item.variant_id,
          productId: item.product_id,
          preservedProps: expandCartLinePropsForEdit(pb, item.properties || {}),
          hydrationSource: source,
          originalBundleId: originalOffer.originalBundleId,
          originalBundleTitle: originalOffer.originalBundleTitle,
        };

        if (family?.bundleTitle) {
          s.cartEdit.bundleRowIndex = resolveBundleRowIndexInCart(
            cart,
            item.key,
            family.bundleTitle
          );
        }

        applyEntryManifest(
          s,
          createMainCartEditEntryManifest({
            lineKey: item.key,
            variantId: item.variant_id,
            bundleRowIndex: s.cartEdit.bundleRowIndex,
            hydrationSource: source,
            hydrationAuthority: HYDRATION_AUTHORITY.LINE,
          }),
          s.cartEdit
        );

        applyMainCartEditSelections(pb, item, readEntryManifest(s), { hydrated, partial });

        let bundleForEdit = null;
        if (originalOffer.originalBundleId && family?.bundleTitle) {
          bundleForEdit = resolveBundleByTitle(family.bundleTitle, window.BUILDER_DATA);
          if (bundleForEdit) {
            hydrateBundleStateFromCart(cart, family, bundleForEdit, s, pb, { skipSeedAnchor: true });
            if (isLineAuthoritativeManifest(readEntryManifest(s))) {
              syncCartEditBundleSlotFromLine(cart, item, family, bundleForEdit, s);
            }
          }
        } else {
          s.bundle.selectedId = null;
        }

        s.cartAddons = {};
        hydrateMainLineAddonsFromCart(cart, item, s);

        if (partial) {
          applyCartEditAuxiliaryPartial(s, partial);
        }

        const mainProduct = window.BUILDER_DATA?.mainProduct;

        let catalogDraft = partial?.catalogDesignLibrary || hydrated.catalogDesignLibrary;
        if (catalogDraft) {
          catalogDraft = enrichCatalogDesignLibraryFromTier1Cart(
            catalogDraft,
            item.properties || {},
            window.BUILDER_DATA?.designLibraryIndex || []
          );
          applyCatalogDesignLibraryToState(s, catalogDraft);
        } else {
          const fields = pb.getPersonalizationFields?.(mainProduct) || [];
          const fromSnapshot = decodeCatalogDesignLibraryFromProps(item.properties || {}, fields);
          if (fromSnapshot) {
            applyCatalogDesignLibraryToState(s, fromSnapshot);
          }
        }

        if (pb.normalizeMainAiImageSelections && mainProduct) {
          pb.normalizeMainAiImageSelections(mainProduct);
        }
        const aiImageDraft = resolveCartEditAiImageDraft(pb, item, mainProduct, partial);
        if (aiImageDraft?.byLabel) {
          hydrateCartEditAiFromDraft('#main-flow-container', aiImageDraft, {
            forceAiReinit: true,
          });
        }
        const aiTextDraft = resolveCartEditAiTextDraft(
          item,
          mainProduct,
          partial,
          s.main?.selections || {}
        );
        if (aiTextDraft?.byLabel) {
          hydrateCartEditAiTextFromDraft('#main-flow-container', aiTextDraft, {
            forceAiReinit: true,
          });
        }
        ensureFinalizeHookInstalled();
      },
    });

    ensureFinalizeHookInstalled();
    scheduleCartEditMediaHydration();
  } catch (err) {
    console.error('[pb-cart-edit] openCartEdit failed', err);
    if (cartEditOpenStarted || window.builderState?.cartEdit) {
      teardownFailedCartEditOpen(err);
    }
  } finally {
    btn.disabled = false;
    btn.removeAttribute('aria-busy');
    btn.textContent = label;
  }
}

if (document.getElementById('pb-cart-edit-portal')) {
  installFinalizeHook();
  if (window.productBuilder) installCartEditHelpers(window.productBuilder);
}

if (typeof window !== 'undefined') {
  window.__pbResolveBuilderCloseMode = resolveBuilderCloseMode;
  window.__pbPresentPendingChildLineCartEdit = presentPendingChildLineCartEdit;
}
